import React from 'react';
import {
    Box,
    Typography
} from '@mui/material';
import { UserPermissionsConfig } from './UserPermissionsConfig';
import { useUserPermissions } from './hooks/useUserPermissions';

export const PermissionsConfigurationStep = ({
    selectedUsers,
    availableUsers,
    userPermissions,
    onUserPermissionsChange,
    originalFiles,
    getDefaultPermissionsForRole,
    currentUserAccess
}) => {
    const { getAccessibleFiles, isFileAccessible } = useUserPermissions(currentUserAccess);
    const accessibleFiles = getAccessibleFiles(originalFiles);

    const getUserById = (userGuid) => {
        return availableUsers.find(user => (user.guid || user.Guid) === userGuid);
    };

    const initializeUserPermissions = (userGuid) => {
        const defaultPermissionsForRole = getDefaultPermissionsForRole('Viewer');
        const filePermissions = {};

        originalFiles.forEach(file => {
            const fileKey = `${file.fileName}_v${file.fileVersion}`;

            if (isFileAccessible(file)) {
                filePermissions[fileKey] = { ...defaultPermissionsForRole };
            } else {
                filePermissions[fileKey] = {
                    canRead: false,
                    canEdit: false,
                    canDeleteOwnVersions: false,
                    canDeleteAllVersions: false
                };
            }
        });

        return {
            role: 'Viewer',
            advancedMode: false,
            customCanManageTeam: false,
            filePermissions
        };
    };

    const handleUserRoleChange = (userGuid, newRole) => {
        onUserPermissionsChange(prev => {
            const currentPermissions = prev[userGuid] || {};
            const defaultPermissionsForRole = getDefaultPermissionsForRole(newRole);

            const updatedFilePermissions = { ...currentPermissions.filePermissions };

            originalFiles.forEach(file => {
                const fileKey = `${file.fileName}_v${file.fileVersion}`;

                if (isFileAccessible(file)) {
                    updatedFilePermissions[fileKey] = { ...defaultPermissionsForRole };
                } else {
                    updatedFilePermissions[fileKey] = {
                        canRead: false,
                        canEdit: false,
                        canDeleteOwnVersions: false,
                        canDeleteAllVersions: false
                    };
                }
            });

            return {
                ...prev,
                [userGuid]: {
                    ...currentPermissions,
                    role: newRole,
                    customCanManageTeam: newRole === 'Admin' || newRole === 'Creator',
                    filePermissions: updatedFilePermissions
                }
            };
        });
    };

    const handleUserAdvancedModeChange = (userGuid, advancedMode) => {
        onUserPermissionsChange(prev => ({
            ...prev,
            [userGuid]: {
                ...prev[userGuid],
                advancedMode
            }
        }));
    };

    const handleUserCustomCanManageTeamChange = (userGuid, canManageTeam) => {
        onUserPermissionsChange(prev => ({
            ...prev,
            [userGuid]: {
                ...prev[userGuid],
                customCanManageTeam: canManageTeam
            }
        }));
    };

    const handleUserFilePermissionChange = (userGuid, fileKey, permissionType, value) => {
        onUserPermissionsChange(prev => {
            const currentUserPerms = prev[userGuid] || {};
            const currentFilePerms = currentUserPerms.filePermissions || {};
            const currentPermissionsForFile = currentFilePerms[fileKey] || {};

            return {
                ...prev,
                [userGuid]: {
                    ...currentUserPerms,
                    filePermissions: {
                        ...currentFilePerms,
                        [fileKey]: {
                            ...currentPermissionsForFile,
                            [permissionType]: value
                        }
                    }
                }
            };
        });
    };

    if (selectedUsers.length === 0) {
        return (
            <Box textAlign="center" py={4}>
                <Typography variant="h6" color="textSecondary">
                    Сначала выберите пользователей
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {selectedUsers.map((userGuid, index) => {
                const user = getUserById(userGuid);
                const userPerms = userPermissions[userGuid] || initializeUserPermissions(userGuid);

                return (
                    <UserPermissionsConfig
                        key={userGuid}
                        user={user}
                        selectedRole={userPerms.role}
                        onRoleChange={(newRole) => handleUserRoleChange(userGuid, newRole)}
                        advancedMode={userPerms.advancedMode}
                        onAdvancedModeChange={(advanced) => handleUserAdvancedModeChange(userGuid, advanced)}
                        customCanManageTeam={userPerms.customCanManageTeam}
                        onCustomCanManageTeamChange={(canManage) => handleUserCustomCanManageTeamChange(userGuid, canManage)}
                        filePermissions={userPerms.filePermissions}
                        onFilePermissionChange={(fileKey, permissionType, value) =>
                            handleUserFilePermissionChange(userGuid, fileKey, permissionType, value)
                        }
                        allFiles={accessibleFiles}
                        getDefaultPermissionsForRole={getDefaultPermissionsForRole}
                        currentUserAccess={currentUserAccess}
                    />
                );
            })}
        </Box>
    );
};
